/**
 * Which workspace this browser was last in (#1014, D-1014-2).
 *
 * A user with more than one workspace needs `/dashboard` to reopen where they
 * were rather than at whichever workspace happens to be first in creation
 * order — otherwise every visit to the bare entry route silently moves them
 * back to their first blog and they have to switch again.
 *
 * ## Why localStorage and not a column on `users`
 *
 * The need is "the same browser reopens where I was", which is exactly what
 * per-viewer storage models: a server preference would mean a DDL apply on the
 * deploy path plus a write on every workspace navigation, to answer a question
 * that is not actually about the account. The cost, accepted and stated: a
 * second device starts at the first workspace. Same trade-off, same seam, as
 * `agentSetupAck.ts` — if it ever becomes a column, this module is the only
 * thing that changes.
 *
 * ## It is a hint, never an authorisation
 *
 * Nothing here is trusted. The value is an opaque id written by whoever last
 * used this browser — possibly a different account entirely — and every reader
 * passes it back through the caller's own workspace list before using it
 * (`getEntryWorkspace`). A stale, foreign or vandalised value therefore
 * degrades to the pre-#1014 behaviour (first workspace), never to access.
 */

export const LAST_WORKSPACE_KEY = "gs-last-workspace";

/**
 * The remembered id, or `null` when there is none.
 *
 * `null` on the server and on any storage failure (Safari private mode, a
 * sandboxed iframe, a browser configured to refuse site data): a preference we
 * cannot read is not a preference to honour, and the fallback is exactly what
 * this app did before the memory existed.
 */
export function readLastWorkspaceId(): string | null {
  try {
    const stored = globalThis.localStorage?.getItem(LAST_WORKSPACE_KEY);
    // Normalised to `null` so callers have one empty value to test, not two:
    // `getItem` answers `null` for an absent key, but a cleared key can also
    // have been written as `""`.
    return stored ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Record the workspace this browser is now in. Swallows storage failures and
 * returns nothing: unlike `acknowledgeAgentSetup`, no caller can act on the
 * difference — a browser that cannot remember simply lands on the first
 * workspace next time, which is a working app, not a broken affordance. Saying
 * so would be noise on a navigation the user did not ask anything of.
 */
export function rememberWorkspaceId(workspaceId: string): void {
  if (!workspaceId) return;
  try {
    globalThis.localStorage?.setItem(LAST_WORKSPACE_KEY, workspaceId);
  } catch {
    // Storage refused (privacy mode, quota). Entry falls back to the first
    // workspace in creation order.
  }
}

/**
 * The choice itself, over a list the caller already has — split out so the
 * route guard can apply the identical preference without a second fetch, and
 * so it is unit-testable without a query client. It lives here, not in
 * `workspaces.ts`, because this module imports nothing: tests that mock
 * `@/lib/workspaces` (and its trpc query options) still run the real choice.
 */
export function pickEntryWorkspace<T extends { workspaceId: string }>(
  workspaces: T[],
): T | null {
  const remembered = readLastWorkspaceId();
  const match = remembered
    ? workspaces.find((workspace) => workspace.workspaceId === remembered)
    : undefined;

  return match ?? workspaces[0] ?? null;
}

/** Testing seam, and the escape hatch a sign-out could use. */
export function forgetLastWorkspaceId(): void {
  try {
    globalThis.localStorage?.removeItem(LAST_WORKSPACE_KEY);
  } catch {
    // Same reasoning as the write.
  }
}
